import type { PropertyValues } from 'lit'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { FormDataController } from '../common/controllers/FormDataController.js'
import { StateAttributesController } from '../common/controllers/StateAttributesController.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { FieldControlMixin } from '../common/mixins/FieldControlMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './OtpField.css'
import { OtpFieldCompleteEvent, OtpFieldInvalidEvent } from './OtpFieldEvents.js'

export { OtpFieldCompleteEvent, OtpFieldInvalidEvent } from './OtpFieldEvents.js'

/** The character sets the field accepts, mirroring Base UI's `validationType`. */
export type OtpFieldType = 'numeric' | 'alphanumeric'

interface OtpValidationConfig {
  /** Pattern for a single cell input, e.g. `'\\d{1}'`. */
  slotPattern: string
  /** Builds the full-value pattern for the hidden input, e.g. `'\\d{6}'`. */
  rootPattern: (length: number) => string
  /** Strips characters not allowed by this type. */
  regexp: RegExp
  /** Virtual-keyboard hint for cells and the hidden input. */
  inputMode: 'numeric' | 'text'
  /** Optional consumer normalization (mirrors Base UI's `normalizeValue`). */
  normalizeValue?: (value: string) => string
}

// Kept as a table (rather than inline branches) so adding Base UI's `alpha` /
// `none` types later is a one-line change.
const OTP_VALIDATION_CONFIG: Record<OtpFieldType, OtpValidationConfig> = {
  numeric: {
    slotPattern: '\\d{1}',
    rootPattern: length => `\\d{${length}}`,
    regexp: /\D/g,
    inputMode: 'numeric',
  },
  alphanumeric: {
    slotPattern: '[a-zA-Z0-9]{1}',
    rootPattern: length => `[a-zA-Z0-9]{${length}}`,
    regexp: /[^a-z0-9]/gi,
    inputMode: 'text',
  },
}

/** Strips all whitespace from a raw OTP string. */
function stripWhitespace(value: string | null | undefined): string {
  return (value ?? '').replace(/\s/g, '')
}

/** Removes characters not permitted by the type's regexp. */
function applyValidation(value: string, config: OtpValidationConfig): string {
  return value.replace(config.regexp, '')
}

/**
 * Normalizes user-entered OTP text: strips whitespace, filters by `type`, then
 * clamps to `length` by Unicode code point so multi-byte characters never split
 * across a cell. Returns the normalized value plus whether any character was
 * rejected (so the caller can fire `value-invalid`).
 */
function normalizeWithDetails(
  value: string | null | undefined,
  length: number,
  config: OtpValidationConfig,
): readonly [value: string, didRejectCharacters: boolean] {
  const stripped = stripWhitespace(value)
  let normalized = applyValidation(stripped, config)
  let didReject = stripped.length > normalized.length

  // Optional consumer normalization (e.g. uppercasing), mirroring Base UI's
  // `normalizeValue`: runs after type filtering, then the result is filtered
  // again so a normalizer can't introduce characters the type disallows.
  if (config.normalizeValue) {
    const custom = config.normalizeValue(normalized)
    didReject ||= normalized.length > custom.length
    normalized = applyValidation(custom, config)
    didReject ||= custom.length > normalized.length
  }

  // Slice by code points so multi-byte characters do not split across slots.
  const maxLength = length < 0 ? 0 : length
  const characters = Array.from(normalized)

  return [
    characters.slice(0, maxLength).join(''),
    didReject || characters.length > maxLength,
  ]
}

/** Convenience wrapper returning only the normalized value. */
function normalize(value: string | null | undefined, length: number, config: OtpValidationConfig): string {
  return normalizeWithDetails(value, length, config)[0]
}

/**
 * Replaces characters starting at `index`, then re-normalizes so paste and
 * multi-character edits stay contiguous and length-bounded.
 */
function replaceValue(
  current: string,
  index: number,
  next: string,
  length: number,
  config: OtpValidationConfig,
): string {
  const normalized = normalize(next, length, config)
  const prefix = current.slice(0, index)
  const suffix = current.slice(index + normalized.length)

  return normalize(`${prefix}${normalized}${suffix}`, length, config)
}

/** Splices a single character out of the value at `index`. */
function removeCharacter(current: string, index: number): string {
  if (index < 0 || index >= current.length) {
    return current
  }

  return `${current.slice(0, index)}${current.slice(index + 1)}`
}

/** Per-instance counter so light-DOM ids stay unique when the host has no `id`. */
let otpFieldCount = 1

/**
 * OTP Field is a segmented one-time-code input: it renders `length` individual
 * character cells plus a visually-hidden input that holds the full value, enabling
 * SMS / keychain autofill and native `pattern`/`required` validation. Typing fills
 * and auto-advances, Backspace clears and moves back, the arrow keys navigate, and
 * pasting a code distributes its characters across the cells.
 *
 * OTP Field is a control only — it has no built-in label, hint or error. Compose
 * it inside a [Field](/components/field/) with a [Field Label](/components/field-label/)
 * (connected with `for`) to give it an accessible name, helper text and an error
 * message, exactly like a native input.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @fires {NordEvent} input - Dispatched whenever the OTP value changes through user interaction (typing, Backspace/Delete, paste, autofill).
 * @fires {NordEvent} change - Dispatched alongside `input` when the committed value changes via user interaction.
 * @fires {OtpFieldCompleteEvent} complete - Dispatched after the value updates when every cell becomes filled.
 * @fires {OtpFieldInvalidEvent} value-invalid - Dispatched when typed or pasted text contained characters rejected by `type`.
 *
 * @localization characterLabel - Per-cell accessible label, given the 1-based cell position and total cell count.
 *
 * @attr {boolean} data-invalid - Present while the field is invalid (mirrors the \`invalid\` property). Style hook for the whole control.
 * @attr {boolean} data-focused - Present while any cell has focus. Style hook for focus-within treatments.
 *
 * @cssprop [--n-otp-field-gap=var(--n-space-s)] - Gap between cells (and between groups when `group-size` is used).
 * @cssprop [--n-otp-field-cell-size=var(--n-space-xl)] - Controls the block-size and inline-size of each square cell. When unset, size maps to the shared control block sizes.
 * @cssprop [--n-otp-field-background=var(--n-color-surface)] - Cell background.
 * @cssprop [--n-otp-field-border-color=var(--n-color-border-strong)] - Cell border color (resting).
 * @cssprop [--n-otp-field-border-radius=var(--n-border-radius-s)] - Cell corner radius.
 * @cssprop [--n-otp-field-font-size=var(--n-font-size-m)] - Cell character font size.
 */
@customElement('nord-otp-field')
export default class OtpField extends DraftComponentMixin(SizeMixin(FieldControlMixin(InputMixin(LitElement)))) {
  /** Stable, unique id base for light-DOM ARIA wiring of this instance. */
  private otpId = `nord-otp-field-${otpFieldCount++}`

  private localize = new LocalizeController<'nord-otp-field'>(this)

  private formData = new FormDataController(this, { value: () => this.value || undefined })

  /** Reflects `invalid` to `data-invalid` and focus-within to `data-focused`. */
  private stateAttributes = new StateAttributesController(
    this,
    () => ({ invalid: this.invalid }),
    { trackFocus: true },
  )

  /** Re-renders when the host's forwarded ARIA labelling attributes change. */
  private ariaObserver?: MutationObserver

  /** Index + value to focus once the async value update has applied. */
  private pendingFocus: { index: number, value: string } | null = null

  /** Value to fire `complete` for once the async value update has applied. */
  private pendingComplete: string | null = null

  /** Whether any cell currently holds focus (drives roving tabindex + data-focused). */
  private focused = false

  /** The cell index that currently has (or last had) focus. */
  private focusedIndex = 0

  /**
   * The number of OTP cells. Must be a positive integer.
   */
  @property({ reflect: true, type: Number }) length = 6

  /**
   * Controls the allowed characters, `inputmode` and `pattern`. `numeric` accepts
   * digits; `alphanumeric` accepts letters and digits.
   */
  @property({ reflect: true }) type: OtpFieldType = 'numeric'

  /**
   * Prevents the user from changing the value while keeping navigation and focus.
   */
  @property({ reflect: true, type: Boolean }) readonly = false

  /**
   * Obscures each cell (renders `type="password"`) for use on shared screens.
   */
  @property({ reflect: true, type: Boolean }) mask = false

  /**
   * Placeholder character or string shown in every empty cell.
   */
  @property({ reflect: true }) placeholder?: string

  /**
   * Marks the field invalid, applying error styling and `aria-invalid` to every
   * cell and the hidden input. Pair with a `nord-field-error` in the Field.
   */
  @property({ reflect: true, type: Boolean }) invalid = false

  // The control's own invalid state for the field context: its `invalid` prop or
  // a built-in `error` (when used standalone).
  protected override get ownInvalid() {
    return this.invalid || super.ownInvalid
  }

  /**
   * The autocomplete token applied to the first cell and the hidden input (all
   * other cells get `autocomplete="off"`). Defaults to `one-time-code` to enable
   * SMS / keychain OTP autofill.
   */
  @property() autocomplete = 'one-time-code'

  /**
   * Renders a decorative separator after every N cells (for example, `3` shows
   * `123-456`). `0` disables grouping. The separator is `aria-hidden` and does
   * not affect the value or navigation.
   */
  @property({ reflect: true, type: Number, attribute: 'group-size' }) groupSize = 0

  /**
   * Whether every cell is filled (`value.length === length`). Read-only and
   * derived from `value`; reflected as the `complete` attribute so consumers can
   * style the completed state.
   */
  get complete(): boolean {
    return this.length > 0 && this._value.length === this.length
  }

  /** Backing store for the normalized value. */
  private _value = ''

  /**
   * The OTP value, for example `'123456'`. Whitespace is stripped, characters are
   * filtered by `type`, and the result is clamped to `length`. Set programmatically
   * to prefill.
   */
  @property()
  // @ts-expect-error - intentionally re-declares the inherited `value` property as a normalizing accessor
  get value(): string {
    return this._value
  }

  set value(next: string) {
    const old = this._value
    const normalized = normalize(next, this.length, this.config)

    if (normalized === old) {
      return
    }

    this._value = normalized
    this.requestUpdate('value', old)
  }

  /**
   * Function that normalizes the value after whitespace and `type` filtering —
   * e.g. `el.normalizeValue = v => v.toUpperCase()`. It runs whenever the field
   * normalizes a value (initial, programmatic, and user edits), inside the
   * component's own pipeline, so live normalization never fights the per-cell
   * focus handling. The result is filtered by `type` again, then clamped to
   * `length`; it should be idempotent since the same value may be normalized
   * more than once. Characters rejected while normalizing are reported through
   * `value-invalid`. Mirrors Base UI's `normalizeValue`.
   */
  @property({ attribute: false }) normalizeValue?: (value: string) => string

  /** The validation config for the current `type`. */
  private get config(): OtpValidationConfig {
    return {
      ...(OTP_VALIDATION_CONFIG[this.type] ?? OTP_VALIDATION_CONFIG.numeric),
      normalizeValue: this.normalizeValue,
    }
  }

  /** Whether `length` is a usable positive integer. */
  private get hasValidLength(): boolean {
    return Number.isInteger(this.length) && this.length > 0
  }

  /** The index of the cell that is currently tabbable (roving tabindex). */
  private get activeIndex(): number {
    const last = Math.max(this.length - 1, 0)

    return this.focused
      ? Math.min(Math.max(this.focusedIndex, 0), last)
      : Math.min(this.value.length, last)
  }

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    // The built-in `<label for>` (from FieldControlMixin) focuses the first cell.
    this.inputId = this.cellId(0)

    // An external label (e.g. nord-field-label) names the control by setting
    // `aria-labelledby`/`aria-label` on the host; forward it to the first cell.
    this.ariaObserver ??= new MutationObserver(() => this.requestUpdate())
    this.ariaObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-label', 'aria-labelledby', 'aria-describedby'],
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.ariaObserver?.disconnect()
    this.ariaObserver = undefined
  }

  // Render in the light DOM so the control participates in the document tree
  // (form labelling, Tailwind), like the other new Nord components.
  protected createRenderRoot() {
    return this
  }

  /** Re-normalize the value when `length` or `type` changes (e.g. clamps it). */
  @observe('length')
  @observe('type')
  protected renormalize() {
    const normalized = normalize(this._value, this.length, this.config)

    if (normalized !== this._value) {
      this._value = normalized
      this.requestUpdate()
    }
  }

  /**
   * Flush queued focus/complete once the value update has actually applied.
   * Runs from `updated()` (after every render) rather than a value observer so
   * it also fires for a re-paste that completes an already-complete value (the
   * value text is unchanged, so a value observer would not run).
   */
  private flushPending() {
    if (this.pendingComplete != null) {
      const value = this.pendingComplete
      this.pendingComplete = null

      if (value === this.value) {
        this.dispatchEvent(new OtpFieldCompleteEvent(value))
      }
    }

    if (this.pendingFocus != null) {
      const pending = this.pendingFocus
      this.pendingFocus = null

      if (pending.value === this.value) {
        this.focusInput(pending.index)
      }
    }
  }

  /** Live list of the rendered cell inputs, in DOM order. */
  private get cells(): HTMLInputElement[] {
    return Array.from(this.querySelectorAll<HTMLInputElement>('input.n-cell'))
  }

  /** Focus a cell by index (clamped), then select its content. */
  private focusInput(index: number) {
    const cells = this.cells
    const target = cells[Math.min(Math.max(index, 0), Math.max(cells.length - 1, 0))]
    target?.focus()
    target?.select()
  }

  /** Queue a focus to run once the value update applies (Lit renders async). */
  private queueFocusInput(index: number, value: string) {
    this.pendingFocus = { index, value }
  }

  /** Focus the field by moving focus into its active cell. */
  focus(options?: FocusOptions) {
    this.cells[this.activeIndex]?.focus(options)
  }

  /**
   * Commit a new value, firing `input` + `change` (and queuing `complete` when
   * the field becomes filled). Returns the committed value, or `null` if nothing
   * changed.
   */
  private setValue(next: string): string | null {
    const normalized = normalize(next, this.length, this.config)
    const wasComplete = this.value.length === this.length

    // A completing change, OR a complete value (re-)committed via paste, signals
    // completion — even when the value text is unchanged.
    const completes = normalized.length === this.length && (!wasComplete || this.lastReason === 'input-paste')

    if (normalized === this.value) {
      if (completes) {
        this.pendingComplete = normalized
        // Value is unchanged so the value observer won't fire — flush manually.
        this.requestUpdate()
      }
      return null
    }

    this.value = normalized

    // Recommended order on a completing edit: 'input', 'change', then 'complete'
    // (queued and flushed after render, so the value is already committed).
    this.dispatchEvent(new NordEvent('input'))
    this.dispatchEvent(new NordEvent('change'))

    this.pendingComplete = completes ? normalized : null

    return normalized
  }

  /** The reason for the in-flight `setValue`, used to detect re-paste completion. */
  private lastReason: 'input-change' | 'input-clear' | 'input-paste' | 'keyboard' = 'input-change'

  /** Report rejected characters to consumers via `value-invalid`. */
  private reportInvalid(raw: string) {
    this.dispatchEvent(new OtpFieldInvalidEvent(raw))
  }

  /**
   * Re-select a cell on mousedown so clicking an already-focused filled cell
   * re-selects its character (typing then replaces it) instead of dropping a
   * caret. Mirrors Base UI's OTPFieldInput onMouseDown.
   */
  private handleMousedown(e: MouseEvent, index: number) {
    if (e.defaultPrevented || this.disabled) {
      return
    }

    e.preventDefault()
    this.focusInput(index)
  }

  private handleInput(e: Event, index: number) {
    // Stop the native input event at the boundary so consumers only ever see the
    // canonical NordEvent('input') dispatched from `setValue` (no double event).
    e.stopPropagation()

    if (this.disabled || this.readonly) {
      return
    }

    const cell = e.target as HTMLInputElement
    const raw = cell.value
    const [digits, didReject] = normalizeWithDetails(raw, this.length, this.config)

    if (didReject) {
      this.lastReason = 'input-change'
      this.reportInvalid(raw)
    }

    if (digits === '') {
      const slotValue = this.value[index] ?? ''
      if (raw === '') {
        // The user deleted the cell's character.
        this.lastReason = 'input-clear'
        this.setValue(removeCharacter(this.value, index))
      }
      else {
        // An invalid character was typed (e.g. a letter in a numeric field) —
        // revert the cell to its committed value (empty for an empty cell, the
        // existing digit for a filled one) so the rejected character never
        // lingers visibly in the field.
        cell.value = slotValue
        cell.select()
      }
      return
    }

    this.lastReason = 'input-change'
    const next = replaceValue(this.value, index, digits, this.length, this.config)
    const committed = this.setValue(next)

    if (committed != null) {
      this.queueFocusInput(Math.min(index + digits.length, this.length - 1), committed)
    }
    else {
      // The edit didn't change the value — e.g. characters typed past a full
      // field, which clamp to a no-op. Base UI's inputs are controlled, so React
      // snaps the cell back to its committed character; the light-DOM cells here
      // aren't, so reset it explicitly to stop the extra keystroke lingering.
      cell.value = this.value[index] ?? ''
      cell.select()
    }
  }

  private handleKeydown(e: KeyboardEvent, index: number) {
    // In disabled mode bail WITHOUT preventing default so vertical arrow keys
    // bubble (matches Base UI 'leaves vertical arrow navigation unhandled').
    if (this.disabled) {
      return
    }

    const firstIndex = 0
    const lastIndex = Math.max(this.length - 1, firstIndex)
    const endTargetIndex = Math.min(this.value.length, lastIndex)
    const hasBoundaryModifier = (e.ctrlKey || e.metaKey) && !e.altKey
    const isRtl = this.resolveDirection() === 'rtl'
    const previousKey = isRtl ? 'ArrowRight' : 'ArrowLeft'
    const nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight'

    // --- Navigation (works in readonly too) ---
    if (e.key === previousKey) {
      this.stopEvent(e)
      this.focusInput(hasBoundaryModifier ? firstIndex : Math.max(firstIndex, index - 1))
      return
    }

    if (e.key === nextKey) {
      this.stopEvent(e)
      this.focusInput(hasBoundaryModifier ? endTargetIndex : Math.min(lastIndex, index + 1))
      return
    }

    if (e.key === 'Home' || e.key === 'ArrowUp') {
      this.stopEvent(e)
      this.focusInput(firstIndex)
      return
    }

    if (e.key === 'End' || e.key === 'ArrowDown') {
      this.stopEvent(e)
      this.focusInput(endTargetIndex)
      return
    }

    // --- Value-changing keys (blocked in readonly) ---
    if (this.readonly) {
      return
    }

    if (e.key === 'Backspace' && hasBoundaryModifier) {
      this.stopEvent(e)
      this.lastReason = 'keyboard'
      if (this.setValue('') != null) {
        this.queueFocusInput(firstIndex, '')
      }
      return
    }

    if (e.key === 'Delete') {
      this.stopEvent(e)
      this.lastReason = 'keyboard'
      const next = removeCharacter(this.value, index)
      const committed = this.setValue(next)
      if (committed != null) {
        this.queueFocusInput(index, committed)
      }
      return
    }

    const slotValue = this.value[index] ?? ''
    const cell = e.currentTarget as HTMLInputElement
    const fullSelection = cell.selectionStart === 0 && cell.selectionEnd === cell.value.length

    // Typing the same character into a fully-selected filled cell would be a
    // no-op change; advance instead so the user isn't stuck.
    if (e.key.length === 1 && fullSelection && slotValue === e.key) {
      this.stopEvent(e)
      if (index < this.length - 1) {
        this.focusInput(index + 1)
      }
      return
    }

    if (e.key === 'Backspace') {
      this.stopEvent(e)
      this.lastReason = 'keyboard'
      const targetIndex = Math.max(firstIndex, index - 1)
      const deleteIndex = slotValue === '' ? targetIndex : index
      const committed = this.setValue(removeCharacter(this.value, deleteIndex))
      if (committed != null) {
        this.queueFocusInput(targetIndex, committed)
      }
    }
  }

  private handlePaste(e: ClipboardEvent, index: number) {
    if (this.disabled || this.readonly) {
      return
    }

    let raw = ''
    try {
      raw = e.clipboardData?.getData('text/plain') ?? ''
    }
    catch {
      if (process.env.NODE_ENV === 'development') {
        console.warn('NORD: nord-otp-field could not read clipboard text during paste.')
      }
      return
    }

    e.preventDefault()

    const [digits, didReject] = normalizeWithDetails(raw, this.length, this.config)

    this.lastReason = 'input-paste'
    if (didReject) {
      this.reportInvalid(raw)
    }

    if (digits === '') {
      return
    }

    const next = replaceValue(this.value, index, digits, this.length, this.config)
    const committed = this.setValue(next)

    if (committed != null) {
      this.queueFocusInput(Math.min(index + digits.length, this.length - 1), committed)
    }
  }

  private handleFocus(e: FocusEvent, index: number) {
    if (this.disabled) {
      return
    }

    // Focusing a cell beyond the first empty slot redirects to the first empty one.
    if (index > this.value.length) {
      this.focusInput(Math.min(this.value.length, this.length - 1))
      return
    }

    this.focusedIndex = index
    this.focused = true
    this.requestUpdate()

    const cell = e.currentTarget as HTMLInputElement
    cell.select()
  }

  private handleBlur(e: FocusEvent) {
    // Ignore focus moving between cells within this field.
    if (e.relatedTarget instanceof Node && this.contains(e.relatedTarget)) {
      return
    }

    this.focused = false
    this.requestUpdate()
  }

  /** The browser focuses the hidden input during autofill — redirect to cell 0. */
  private handleHiddenFocus() {
    this.focusInput(0)
  }

  /** SMS autofill writes the whole code into the hidden input. */
  private handleHiddenInput(e: Event) {
    e.stopPropagation()

    if (this.disabled || this.readonly) {
      return
    }

    const input = e.target as HTMLInputElement
    const [digits, didReject] = normalizeWithDetails(input.value, this.length, this.config)

    this.lastReason = 'input-change'
    if (didReject) {
      this.reportInvalid(input.value)
    }

    const committed = this.setValue(digits)

    if (committed != null && committed !== '') {
      this.queueFocusInput(committed.length - 1, committed)
    }
  }

  /** Resolve the effective text direction from the host or document. */
  private resolveDirection(): 'ltr' | 'rtl' {
    const dir = this.closest('[dir]')?.getAttribute('dir')
      ?? (typeof document !== 'undefined' ? document.documentElement.getAttribute('dir') : null)
    return dir === 'rtl' ? 'rtl' : 'ltr'
  }

  /** preventDefault + stopPropagation, like Base UI's `stopEvent`. */
  private stopEvent(e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  /** The id for a given cell — derived so cell ids stay unique and never collide with the host id. */
  private cellId(index: number): string {
    const base = this.getAttribute('id') ?? this.otpId
    return `${base}-${index + 1}`
  }

  render() {
    if (!this.hasValidLength) {
      return nothing
    }

    const config = this.config
    const value = this.value
    const activeIndex = this.activeIndex
    const groupId = `${this.otpId}-group`

    // Naming/description resolved by the FieldControlMixin getters (field context
    // in a field, built-in label/hint/error standalone, or host aria-* fallback).
    // Cell 0 inherits the shared label and the group is named by it.
    const ariaLabel = this.controlAriaLabel
    const ariaLabelledby = this.controlAriaLabelledby
    const ariaDescribedby = this.controlAriaDescribedby

    const cells = []
    for (let i = 0; i < this.length; i++) {
      cells.push(this.renderCell(i, value, config, activeIndex, ariaLabel, ariaLabelledby))

      // Decorative separators chunk the code, e.g. 123-456.
      if (this.groupSize > 0 && (i + 1) % this.groupSize === 0 && i < this.length - 1) {
        cells.push(html`<span class="n-separator" aria-hidden="true"></span>`)
      }
    }

    return html`
      ${this.renderLabel()}
      <div
        class="n-otp-field"
        id=${groupId}
        role="group"
        aria-label=${ifDefined(ariaLabel)}
        aria-labelledby=${ifDefined(ariaLabelledby)}
        aria-describedby=${ifDefined(ariaDescribedby)}
      >
        ${cells}
      </div>
      <input
        class="n-hidden-input"
        type="text"
        aria-hidden="true"
        tabindex="-1"
        .value=${value}
        autocomplete=${this.autocomplete}
        inputmode=${config.inputMode}
        minlength=${this.length}
        maxlength=${this.length}
        pattern=${config.rootPattern(this.length)}
        ?disabled=${this.effectiveDisabled}
        ?readonly=${this.readonly}
        ?required=${this.required}
        aria-invalid=${ifDefined(!this.effectiveDisabled && this.effectiveInvalid ? 'true' : undefined)}
        @focus=${this.handleHiddenFocus}
        @input=${this.handleHiddenInput}
      />
      ${this.renderError()}
      ${this.isHintBelow ? this.renderHint() : nothing}
    `
  }

  private renderCell(
    index: number,
    value: string,
    config: OtpValidationConfig,
    activeIndex: number,
    ariaLabel: string | undefined,
    ariaLabelledby: string | undefined,
  ) {
    const slotValue = value[index] ?? ''
    const filled = slotValue !== ''

    // Cell 0 inherits the shared field label; later cells get a per-cell label so
    // AT announces which slot is focused.
    const cellAriaLabel = index === 0 ? undefined : this.localize.term('characterLabel', index + 1, this.length)
    const cellAriaLabelledby = index === 0 ? ariaLabelledby : undefined
    const cellAriaLabelInherited = index === 0 ? ariaLabel : undefined

    return html`
      <input
        class="n-cell"
        id=${this.cellId(index)}
        .value=${slotValue}
        type=${this.mask ? 'password' : 'text'}
        inputmode=${config.inputMode}
        autocomplete=${index === 0 ? this.autocomplete : 'off'}
        autocorrect="off"
        spellcheck="false"
        enterkeyhint=${index === this.length - 1 ? 'done' : 'next'}
        maxlength=${ifDefined(index === 0 ? this.length : undefined)}
        pattern=${config.slotPattern}
        placeholder=${ifDefined(this.placeholder)}
        tabindex=${activeIndex === index ? 0 : -1}
        ?disabled=${this.effectiveDisabled}
        ?readonly=${this.readonly}
        aria-label=${ifDefined(cellAriaLabel ?? cellAriaLabelInherited)}
        aria-labelledby=${ifDefined(cellAriaLabel == null ? cellAriaLabelledby : undefined)}
        aria-invalid=${ifDefined(!this.effectiveDisabled && this.effectiveInvalid ? 'true' : undefined)}
        data-filled=${ifDefined(filled ? '' : undefined)}
        @mousedown=${(e: MouseEvent) => this.handleMousedown(e, index)}
        @input=${(e: Event) => this.handleInput(e, index)}
        @keydown=${(e: KeyboardEvent) => this.handleKeydown(e, index)}
        @paste=${(e: ClipboardEvent) => this.handlePaste(e, index)}
        @focus=${(e: FocusEvent) => this.handleFocus(e, index)}
        @blur=${(e: FocusEvent) => this.handleBlur(e)}
      />
    `
  }

  /** Mirror state to host data-* attributes and flush queued focus/complete. */
  protected updated(changed: PropertyValues) {
    super.updated(changed)
    this.toggleAttribute('complete', this.complete)
    this.toggleAttribute('data-complete', this.complete)
    this.toggleAttribute('data-filled', this.value.length > 0)
    this.toggleAttribute('data-disabled', this.disabled)
    this.toggleAttribute('data-readonly', this.readonly)
    this.toggleAttribute('data-required', this.required)
    // `data-invalid` and `data-focused` are managed by `stateAttributes`.

    this.flushPending()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-otp-field': OtpField
  }
}
